import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import { ButtonV3 } from 'components/ComponentV2';
import { Form } from 'components/form';
import { Row } from 'components/layout';
import { DragAndDrop } from 'pages/society-profile/EditSocietyProfileInfo/CustomLinksDragAndDrop.component';
import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { useCommonList } from 'lib/contexts/common-list-context';
import { CompanyRequest } from 'lib/models/company';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';

import { CompanyProfileReturnType } from '../../../hooks/UseCompanyProfile.hook';
import ArticlesDetails from '../ArticlesDetails';
import BasicDetails from '../BasicDetails';
import BenefitsDetails from '../BenefitsDetails';
import CompanyDetails from '../CompanyDetails';
import CoverImage from '../CoverImage';
import { Gamification } from '../Gamification/Gamification.component';
import IdentityDetails from '../IdentityDetails';
import SectionWrapper from '../SectionWrapper';
import SocialLinks from '../SocialLinks';

import styles from './EditCompanyProfileInfo.module.scss';

interface EditCompanyProfileInfoProps extends CompanyProfileReturnType {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  reference: React.Ref<unknown> | undefined;
  setIshowSaveModal: (value: boolean) => void;
  onboarding?: boolean;
}

export const EditCompanyProfileInfo: React.FC<EditCompanyProfileInfoProps> =
  memo(
    ({
      className = '', // custom class name
      style, // custom style
      company,
      companyBenefitStat: benefit_stats,
      companyCultuteStat: culture_stats,
      companyInsights: insights,
      companyCustomLinks,
      onCompanyUpdate,
      reference,
      setIshowSaveModal,
      onboarding,
      isLoading,
    }: EditCompanyProfileInfoProps) => {
      const { industryOptions, employeesCountRange } = useCommonList();
      const { updateUserApiResponse } = useAuth();

      const router = useRouter();

      const [
        focusedIncompleteSectionBanner,
        setFocusedIncompleteSectionBanner,
      ] = React.useState<number | undefined>();

      const [showSectionProgressBanner, setShowSectionProgressBanner] =
        React.useState<boolean>(false);

      const [showCompleteProgressBanner, setShowCompleteProgressBanner] =
        React.useState(true);

      useImperativeHandle(reference, () => ({
        submitForm() {
          handleSubmit(onSubmit)();
        },
      }));
      const contentClassNames = cn(
        {
          [styles.EditCompanyProfileInfo]: true,
        },
        className
      );

      const {
        name,
        address,
        banner_url,
        icon_url,
        instagram_url,
        facebook_url,
        linkedin_url,
        website,
        short_description,
        description,
        founded_year,
        industry,
        sub_industries,
        employees_count,
        custom_industry_name,
      } = company;

      const defaultValues = {
        logo: [],
        banner: [],
        name,
        description,
        short_description,
        address,
        instagram_url,
        facebook_url,
        linkedin_url,
        website,
        remove_banner: false,
        founded_year: founded_year?.toString(),
        employees_count,
        industry_id: industry?.data?.id,
        sub_industry_id: sub_industries?.data?.length
          ? sub_industries?.data[0].attributes.id
          : undefined,
        culture_stats_attributes: culture_stats,
        benefit_stats_attributes: benefit_stats,
        insights_attributes: insights,
        custom_industry_name,
        custom_links_attributes: companyCustomLinks,
      };

      const methods = useForm<CompanyRequest>({
        defaultValues,
      });
      const {
        watch,
        handleSubmit,
        setValue,
        control,
        formState: { isDirty },
      } = methods;

      useEffect(() => {
        setValue('benefit_stats_attributes', benefit_stats);
      }, [benefit_stats]);

      useEffect(() => {
        setValue('culture_stats_attributes', culture_stats);
      }, [culture_stats]);

      useEffect(() => {
        setValue('insights_attributes', insights);
      }, [insights]);

      useEffect(() => {
        setValue('custom_links_attributes', companyCustomLinks);
      }, [companyCustomLinks]);

      const {
        fields,
        append,
        remove,
        move,
        update: updateCustomLink,
      } = useFieldArray({
        control,
        name: 'custom_links_attributes',
      });

      const watchAllFields = watch();

      useEffect(() => {
        setIshowSaveModal(isDirty);
      }, [isDirty]);

      const onSubmit = (data: CompanyRequest) => {
        onCompanyUpdate(data);
        if (onboarding) {
          const flow_state = ONBOARDING_FLOW_STATE.POST_5;
          const recruiter = { flow_state };
          apiInstance
            .patch(API_ROUTES.RECRUITER_SIGNUP, {
              recruiter,
            })
            .then((response) => {
              updateUserApiResponse(response.data);
              router.push(PAGE_ROUTES.RECRUITER_ONBOARDING);
            });
        }
      };

      const onDeleteCoverPhoto = async () => {
        setValue('remove_banner', true);
      };

      const profileItem = [
        {
          id: 'cover_image',
          title: 'Cover photo',
          desc: 'Company pages with photos are more intriguing for students and can increase viewing time up to 30%.',
          isCompleted:
            (!!banner_url || !!watchAllFields.banner?.length) &&
            !watchAllFields.remove_banner,
        },
        {
          id: 'basic_info',
          title: 'Basic info',
          desc: 'Fill out missing information to increase your chances of attracting best candidates!',
          isCompleted:
            (!!icon_url || !!watchAllFields.logo?.length) &&
            !!watchAllFields.name &&
            !!watchAllFields.short_description &&
            !!watchAllFields.website,
        },
        {
          id: 'company_identity',
          title: 'Company identity',
          desc: 'Get more aligned candidates and help them make stronger applications.',
          isCompleted:
            !!watchAllFields.description &&
            !!watchAllFields.culture_stats_attributes?.filter(
              (item) => !item._destroy
            ).length,
        },
        {
          id: 'social_links',
          title: 'Social media links',
          desc: 'Add at least 1 social link to spruce up your page. Companies with an active social media presence are more attractive to students.',
          isCompleted:
            !!watchAllFields.instagram_url ||
            !!watchAllFields.facebook_url ||
            !!watchAllFields.linkedin_url,
        },
        {
          id: 'benefits',
          title: 'Benefits',
          desc: 'Candidates are more likely to apply for a job that offers competitive benefits.',
          isCompleted: !!watchAllFields.benefit_stats_attributes?.filter(
            (item) => !item._destroy
          ).length,
        },
        {
          id: 'articles',
          desc: "Sharing your company's accomplishments gets students excited about working for you!",
          title: 'Articles and News',
          isCompleted: !!watchAllFields.insights_attributes?.filter(
            (item) => !item._destroy
          ).length,
        },
        {
          id: 'company_details',
          title: 'Company Details',
          desc: 'Fill out missing information to increase your chances of attracting best candidates!',
          isCompleted:
            !!watchAllFields.founded_year &&
            !!watchAllFields.address &&
            !!watchAllFields.industry_id &&
            !!watchAllFields.employees_count,
        },
      ];

      const profileCompleteCount = profileItem.filter(
        (item) => item.isCompleted === true
      ).length;

      const nonCompletedSection = useMemo(() => {
        return profileItem.filter((item) => item.isCompleted === false);
      }, []);

      const profileCompletePercent =
        (profileCompleteCount / profileItem.length) * 100;

      const profileComplete = profileCompletePercent === 100;

      //Handle ok button click on progress banner
      const handleProfileCompletionButton = () => {
        setShowSectionProgressBanner(false);
        setShowCompleteProgressBanner(false);
        setFocusedIncompleteSectionBanner(undefined);
      };

      const handleShowMeButton = () => {
        setShowSectionProgressBanner(true);
        setShowCompleteProgressBanner(false);
        setFocusedIncompleteSectionBanner(0);

        const element = document.getElementById(
          `section_${nonCompletedSection[0]?.id}`
        );
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      //Handle next button click on progress banner
      const handleNextButton = () => {
        const focusIndex =
          focusedIncompleteSectionBanner === undefined
            ? 0
            : focusedIncompleteSectionBanner + 1;
        setFocusedIncompleteSectionBanner(focusIndex);

        const element = document.getElementById(
          `section_${nonCompletedSection[focusIndex].id}`
        );
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      const isToShowBanner =
        showSectionProgressBanner === true &&
        nonCompletedSection.length > 0 &&
        focusedIncompleteSectionBanner !== undefined;

      const showNextButton =
        focusedIncompleteSectionBanner !== undefined
          ? nonCompletedSection.length > focusedIncompleteSectionBanner + 1
          : false;

      return (
        <div
          className={contentClassNames}
          style={style}
          data-testid="EditCompanyProfileInfo"
        >
          <FormProvider {...methods}>
            <Form
              autoComplete="off"
              className={styles.EventsNewEvent}
              onSubmit={handleSubmit(onSubmit)}
              id="company-form"
            >
              <div className={styles.CompanyContent}>
                {showCompleteProgressBanner && (
                  <Gamification
                    profileCompletePercent={profileCompletePercent}
                    nonCompletedSection={
                      profileItem.length - profileCompleteCount
                    }
                    handleProfileCompletionButton={
                      handleProfileCompletionButton
                    }
                    showMeButton={!profileComplete}
                    handleShowMeButton={handleShowMeButton}
                    title="Complete your profile"
                    description="Fill out missing information to increase your chances of attracting best candidates!"
                    className={styles.Gamification}
                  />
                )}

                <CoverImage
                  banner={banner_url}
                  onDeleteCoverPhoto={onDeleteCoverPhoto}
                  progressDetails={profileItem[0]}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  profileCompletePercent={profileCompletePercent}
                  showProgressBanner={
                    isToShowBanner &&
                    focusedIncompleteSectionBanner != undefined &&
                    nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
                      profileItem[0].id
                  }
                  handleNextButton={handleNextButton}
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showNextButton={showNextButton}
                />
                <BasicDetails
                  logo={icon_url}
                  progressDetails={profileItem[1]}
                  sectionClass={styles.SectionShadow}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  profileCompletePercent={profileCompletePercent}
                  showProgressBanner={
                    isToShowBanner &&
                    focusedIncompleteSectionBanner != undefined &&
                    nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
                      profileItem[1].id
                  }
                  handleNextButton={handleNextButton}
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showNextButton={showNextButton}
                />
                <IdentityDetails
                  progressDetails={profileItem[2]}
                  sectionClass={styles.SectionShadow}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  profileCompletePercent={profileCompletePercent}
                  showProgressBanner={
                    isToShowBanner &&
                    focusedIncompleteSectionBanner != undefined &&
                    nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
                      profileItem[2].id
                  }
                  handleNextButton={handleNextButton}
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showNextButton={showNextButton}
                />
                <SectionWrapper
                  title="Links"
                  description="Add few links to your page to help boost their discovery."
                  subDetails={
                    <p className={styles.EditCompanyProfileInfoDesc}>
                      Example: <mark>🚀</mark>
                      <mark>Our careers page</mark>
                      <mark>https://www.example.com</mark>
                    </p>
                  }
                  addButtonLabel="Add link"
                  onAdd={() => {
                    append({
                      title: '',
                      link: '',
                      emoji: '',
                    });
                  }}
                  className={styles.customLinksSection}
                  contentClassName={styles.dragContent}
                  buttonClassName={styles.dragButton}
                  hasData={fields?.length ? true : false}
                >
                  <Row
                    className={cn(styles.sectionRow, styles.sectionRowBlock)}
                  >
                    <DragAndDrop
                      fields={fields}
                      updateCustomLink={updateCustomLink}
                      remove={remove}
                      move={move}
                      name={'custom_links_attributes'}
                    />
                  </Row>
                </SectionWrapper>
                <SocialLinks
                  progressDetails={profileItem[3]}
                  sectionClass={styles.SectionShadow}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  profileCompletePercent={profileCompletePercent}
                  showProgressBanner={
                    isToShowBanner &&
                    focusedIncompleteSectionBanner != undefined &&
                    nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
                      profileItem[3].id
                  }
                  handleNextButton={handleNextButton}
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showNextButton={showNextButton}
                />
                <BenefitsDetails
                  progressDetails={profileItem[4]}
                  sectionClass={styles.SectionShadow}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  profileCompletePercent={profileCompletePercent}
                  showProgressBanner={
                    isToShowBanner &&
                    focusedIncompleteSectionBanner != undefined &&
                    nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
                      profileItem[4].id
                  }
                  handleNextButton={handleNextButton}
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showNextButton={showNextButton}
                />
                <ArticlesDetails
                  progressDetails={profileItem[5]}
                  sectionClass={styles.SectionShadow}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  profileCompletePercent={profileCompletePercent}
                  showProgressBanner={
                    isToShowBanner &&
                    focusedIncompleteSectionBanner != undefined &&
                    nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
                      profileItem[5].id
                  }
                  handleNextButton={handleNextButton}
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showNextButton={showNextButton}
                />
                <CompanyDetails
                  employeesCountRange={employeesCountRange}
                  industryOptions={industryOptions}
                  progressDetails={profileItem[6]}
                  sectionClass={styles.SectionShadow}
                  nonCompletedSection={
                    profileItem.length - profileCompleteCount
                  }
                  profileCompletePercent={profileCompletePercent}
                  showProgressBanner={
                    isToShowBanner &&
                    focusedIncompleteSectionBanner != undefined &&
                    nonCompletedSection[focusedIncompleteSectionBanner]?.id ===
                      profileItem[6].id
                  }
                  handleNextButton={handleNextButton}
                  handleProfileCompletionButton={handleProfileCompletionButton}
                  showNextButton={showNextButton}
                />
              </div>
              <ButtonV3
                type="submit"
                isLoading={isLoading}
                className={styles.buttons}
              >
                Save changes
              </ButtonV3>
            </Form>
          </FormProvider>
        </div>
      );
    }
  );

EditCompanyProfileInfo.displayName = 'EditCompanyProfileInfo';
